import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import { alpha, useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { operationLabelOf } from '../operationNameLabel'
import { raisedColor } from '../detailSurfaces'
import { CONTROL_RADIUS } from '../detailControls'
import { EMPTY_DASH, ColumnHeader } from '../detailShared'

// Label + value pair used inside the row expansions (pasada 24). Read-only by construction: this
// block never writes (RN11/S189).
const DetailDatum = ({ label, testId, children }) => (
  <Box>
    <Typography variant='caption' sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography data-testid={testId} variant='body2' sx={{ fontWeight: 700 }}>{children}</Typography>
  </Box>
)

// The shop-floor reports of the whole order, in time order, paginated by the container.
const OrderReportsList = ({ reports, reportsStatus, hasMoreReports, loadingMoreReports, loadMoreReportsFailed, loadReports, loadMoreReports }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const { formatDateTime } = useDateFormatter()
  const [expandedReportId, setExpandedReportId] = useState(null)

  return (
    <Box data-testid='reports-list' sx={{ mt: 3 }}>
      {/* ⚠️ CON TÍTULO, por decisión del PO del 23/09: salía sin él y no se sabía qué era. Es el
      registro de TODA LA ORDEN en orden de tiempo, que no es la pestaña «Reportes» de cada
      operación —aquélla filtra los de una—, y el título lo dice para que no se lean como la
      misma lista dos veces. Con el mismo rótulo de eyebrow que «Advance per operation». */}
      <Typography data-testid='reports-list-title' variant='caption' sx={{ fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'text.secondary', display: 'block', mb: 1 }}>
        {t_('Shop-floor reports of the order')}
      </Typography>
      {reportsStatus === 'loading' && reports.content.length === 0 && (
        <Typography data-testid='reports-loading' variant='body2' sx={{ color: 'text.secondary' }}>{t_('Loading...')}</Typography>
      )}
      {reportsStatus === 'error' && reports.content.length === 0 && (
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Typography data-testid='reports-error' variant='body2' sx={{ color: 'error.main' }}>{t_('Could not load the reports')}</Typography>
          <Button data-testid='reports-retry' size='small' variant='outlined' onClick={loadReports} sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS }}>{t_('Retry')}</Button>
        </Stack>
      )}
      {reportsStatus === 'ready' && reports.content.length === 0 && (
        <Typography data-testid='no-reports' variant='body2' sx={{ color: 'text.secondary' }}>{t_('No reports yet')}</Typography>
      )}
      {reports.content.length > 0 && (
        <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` }}>
          <Table size='small' aria-label={t_('Shop-floor reports of the order')}>
            <TableHead>
              <TableRow sx={{ backgroundColor: raisedColor(theme) }}>
                <TableCell><ColumnHeader>{t_('Moment')}</ColumnHeader></TableCell>
                <TableCell><ColumnHeader>{t_('Operation')}</ColumnHeader></TableCell>
                <TableCell><ColumnHeader>{t_('Worker')}</ColumnHeader></TableCell>
                <TableCell><ColumnHeader>{t_('Device')}</ColumnHeader></TableCell>
                <TableCell><ColumnHeader>{t_('Quantity')}</ColumnHeader></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.content.map((report) => (
                <React.Fragment key={report.reportId}>
                  <TableRow data-testid={`report-row-${report.reportId}`} hover>
                    <TableCell>
                      <Typography data-testid='report-moment' variant='body2' sx={{ fontWeight: 700 }}>{formatDateTime(report.reportedAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      {/* S354 — ADDENDUM 20 (E26): N11 es la SEXTA y última puerta del renombrado, y
                      la segunda que llegó a pintarse vacía. Misma regla y MISMA función que el
                      resto de la ficha. */}
                      <Typography data-testid='report-operation' variant='body2' sx={{ color: 'text.secondary' }}>{operationLabelOf(report.operation)}</Typography>
                    </TableCell>
                    <TableCell>
                      {/* ⚠️ RN8 — un reporte SIN OPERARIO existe: los hay registrados sin nadie
                      logueado. Dejarlo en blanco no dice «no hay operario», no dice nada, y
                      en una tabla se lee como una celda que se ha perdido. */}
                      <Typography data-testid='report-worker' variant='body2' sx={{ color: 'text.secondary' }}>{report.worker?.workerName || EMPTY_DASH}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography data-testid='report-device' variant='body2' sx={{ color: 'text.secondary' }}>{report.device?.name || EMPTY_DASH}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box data-testid='report-quantities'>
                        {report.quantities.map((quantity) => (
                          <Typography key={quantity.qualityTypeId} data-testid={`report-quantity-${quantity.qualityTypeId}`} component='span' variant='body2' sx={{ mr: 1.5 }}>
                            {`${quantity.qualityTypeName}: ${quantity.quantity}`}
                          </Typography>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align='right'>
                      <Button
                        data-testid={`expand-report-${report.reportId}`} onClick={() =>
                          setExpandedReportId(expandedReportId === report.reportId ? null : report.reportId)}
                        size='small'
                        sx={{ minWidth: 0, textTransform: 'none' }}
                      >
                        {t_('Details')}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expandedReportId === report.reportId && (
                    <TableRow>
                      <TableCell colSpan={6} data-testid={`report-expansion-${report.reportId}`} sx={{ backgroundColor: raisedColor(theme) }}>
                        {/* Verificado por el hub (pasada 24): el desplegable SÍ se abría, pero sólo
                        contenía un párrafo vacío —el comentario— y parecía que no hacía nada.
                        Por contrato (E8, `contrato-sun.md:709`) `inspections` viaja SIEMPRE
                        vacío e `incidence`/`documents` ausentes: no hay fuente en el modelo de
                        datos y S174 quedó recortado a comentario y cantidad. Así que el panel
                        muestra lo que el reporte SÍ trae, y dice explícitamente lo que aún no
                        existe en vez de dejar un hueco en blanco. */}
                        <Stack direction='row' spacing={4} flexWrap='wrap' useFlexGap sx={{ mb: 1.5 }}>
                          <DetailDatum label={t_('Moment')}>{formatDateTime(report.reportedAt)}</DetailDatum>
                          {/* El respaldo es la clave de ausencia ('Not reported' desde el addendum
                          30 A) y NO el código vacío: aquí el dato que falta es del reporte, no
                          del servidor —que tiene su propia frase, 'Not reported by the server'. */}
                          <DetailDatum label={t_('Operation')} testId='report-expansion-operation'>{operationLabelOf(report.operation) || t_('Not reported')}</DetailDatum>
                          <DetailDatum label={t_('Worker')}>{report.worker?.workerName || EMPTY_DASH}</DetailDatum>
                          <DetailDatum label={t_('Device')}>{report.device?.name || EMPTY_DASH}</DetailDatum>
                          <DetailDatum label={t_('Quantity')}>
                            {report.quantities.map((quantity) => `${quantity.qualityTypeName}: ${quantity.quantity}`).join(' · ')}
                          </DetailDatum>
                        </Stack>

                        <Typography variant='caption' sx={{ fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary' }}>
                          {t_('Comment')}
                        </Typography>
                        <Typography data-testid='report-comment' variant='body2' sx={{ mb: 1.5, color: report.comment ? 'text.primary' : 'text.secondary' }}>
                          {report.comment || t_('No comment')}
                        </Typography>

                        {/* ⚠️⚠️ SE QUITA EL VEREDICTO POR ÍTEM, que esta línea pintaba entre
                        paréntesis. `inspections[].result` EXISTE en el DTO y NO SE LLENA
                        NUNCA: el lector de Sun tiene una puerta declarada y escalada —«No
                        data-model correlation exists from a FabricationOrderReportQuantity
                        row to an InspectionReport»— porque los autocontroles correlacionan
                        con (OF, job), no con un reporte.
                        O sea que ese paréntesis solo podía decir «(undefined)» o, peor,
                        repetir el veredicto GLOBAL del informe en cada ítem: un informe NOK
                        con tres ítems no dice cuál falló, y tres ítems marcados NOK afirman
                        que fallaron los tres. Decisión del PO: el ítem con su valor MEDIDO
                        y sin estado.
                        📌 Es `StepDto.worker` otra vez — un campo que existe para que se vea
                        que no hay dato, y que alguien acaba leyendo como que algún día se
                        llena. Aquí ya no se lee. */}
                        {report.inspections.map((inspection, index) => (
                          <Typography key={`${inspection.featureName}-${index}`} data-testid={`report-inspection-${index}`} variant='body2'>
                            {`${inspection.featureName}: ${inspection.value} ${inspection.unit}`.trim()}
                          </Typography>
                        ))}
                        {Object.keys(report.incidence || {}).length > 0 && (
                          <Typography data-testid='report-incidence' variant='body2'>{report.incidence.title}</Typography>
                        )}
                        {report.documents.map((document) => (
                          <Typography key={document.documentId} data-testid={`report-document-${document.documentId}`} variant='body2'>
                            {document.name}
                          </Typography>
                        ))}
                        {report.inspections.length === 0 && report.documents.length === 0 && Object.keys(report.incidence || {}).length === 0 && (
                          <Typography data-testid={`report-no-extra-${report.reportId}`} variant='caption' sx={{ color: 'text.secondary' }}>
                            {t_('Self-checks, incidence and documents are not recorded per report yet')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {hasMoreReports && (
        <Button data-testid='load-more-reports' onClick={loadMoreReports} disabled={loadingMoreReports} sx={{ mt: 1, textTransform: 'none' }}>
          {loadingMoreReports ? t_('Loading...') : t_('Load more')}
        </Button>
      )}
      {loadMoreReportsFailed && (
        <Typography data-testid='load-more-reports-error' variant='body2' sx={{ color: 'error.main' }}>{t_('Could not load the reports')}</Typography>
      )}
    </Box>
  )
}

export default OrderReportsList
